import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

const HIDE_DELAY_MS = 150;
const GAP_PX = 6;

type RequiredCountsHintProps = {
  kind: "machines" | "operators";
  enabled: boolean;
  children: ReactNode;
};

// The count line itself is the trigger (dotted underline), so the hint adds no icon noise.
// Fixed-positioned like ProductHoverCard so the scrolling per-app list cannot clip it.
export const RequiredCountsHint = ({ kind, enabled, children }: RequiredCountsHintProps) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLSpanElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = useId();

  // Below the trigger so the figures it explains stay visible; flips above near the viewport bottom.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const rect = anchor.getBoundingClientRect();
    const { width, height } = tip.getBoundingClientRect();
    const margin = 16;
    const left = Math.max(
      margin,
      Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - margin),
    );
    const below = rect.bottom + GAP_PX;
    const fitsBelow = below + height <= window.innerHeight - margin;
    setPos({ left, top: fitsBelow ? below : Math.max(margin, rect.top - height - GAP_PX) });
  }, [open]);

  if (!enabled) return <>{children}</>;

  const show = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setOpen(true);
  };
  const hide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  return (
    <span onMouseEnter={show} onMouseLeave={hide}>
      <button
        ref={anchorRef}
        type="button"
        className="cursor-help rounded-sm underline decoration-dotted decoration-blue_grey/60 underline-offset-[3px] transition-colors hover:decoration-penn-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-penn-500"
        aria-describedby={open ? tipId : undefined}
        onFocus={show}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        {children}
      </button>
      {open && (
        <span
          ref={tipRef}
          id={tipId}
          role="tooltip"
          style={pos ?? { visibility: "hidden" }}
          className="fixed z-50 flex w-64 max-w-[calc(100vw-2rem)] flex-col gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left text-xs leading-snug text-blue_grey shadow-soft"
        >
          {kind === "machines" ? (
            <span>
              <strong className="font-semibold text-blue_grey1">Required machines</strong> = machine hours needed per year ÷ (working days × hours per day × OEE), rounded up.
            </span>
          ) : (
            <>
              <span>
                <strong className="font-semibold text-blue_grey1">Required operators</strong> = operator hours needed per year ÷ operator hours available per year (working days × hours per day; × OEE on manually loaded machines), rounded up.
              </span>
              <span>On manually loaded machines each machine needs one operator, so both numbers match.</span>
            </>
          )}
          <span>The full calculation is in the Cost Analysis export (.xlsx).</span>
        </span>
      )}
    </span>
  );
};
